import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, RotateCcw, PlusCircle, ExternalLink, Copy, Check, KeyRound } from "lucide-react";
import { demoApi } from "./api";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { usePermission } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

const DEMO_USER_PUBLIC_URL = "https://mixcore.club";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-CL", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function DemoScreen() {
  const queryClient = useQueryClient();
  const canSeed = usePermission(PERMISSIONS.DEMO_SEED);
  const canReset = usePermission(PERMISSIONS.DEMO_RESET);
  const canRevealPassword = usePermission(PERMISSIONS.DEMO_REVEAL_PASSWORD);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<"email" | "password" | null>(null);

  const statusQuery = useQuery({
    queryKey: ["demo", "status"],
    queryFn: () => demoApi.status(),
  });

  const seedMutation = useMutation({
    mutationFn: () => demoApi.seed(),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ["demo", "status"] });
    },
    onError: (err: any) => {
      setActionError(err?.response?.data?.message || err?.message || "Error al crear la cuenta demo.");
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => demoApi.reset(),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ["demo", "status"] });
    },
    onError: (err: any) => {
      setActionError(err?.response?.data?.message || err?.message || "Error al resetear la cuenta demo.");
    },
  });

  const copyCredential = async (field: "email" | "password") => {
    try {
      const creds = await demoApi.credentials();
      const value = field === "email" ? creds.email : creds.password;
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setActionError(null);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err: any) {
      setActionError(
        err?.response?.data?.message || err?.message || "No se pudieron leer las credenciales."
      );
    }
  };

  if (statusQuery.isLoading) return <LoadingBlock label="Cargando estado de la cuenta demo…" />;
  if (statusQuery.isError) return <ErrorBlock onRetry={() => statusQuery.refetch()} />;

  const status = statusQuery.data!;
  const exists = status.exists;

  const handleSeed = async () => {
    const ok = await confirmAction({
      title: "Crear cuenta demo",
      message:
        "Esto crea el user sintético demo@mixcore.demo + su club + 5 eventos placeholder. El password se lee de MIXCORE_DEMO_USER_PASSWORD en Secret Manager. Idempotente: si ya existe, no hace nada.",
      confirmLabel: "Crear",
      tone: "success",
    });
    if (ok === true) seedMutation.mutate();
  };

  const handleReset = async () => {
    const confirmText = await confirmAction({
      title: "Resetear contenido del demo",
      message:
        "Esto borra TODOS los eventos del club demo (placeholders + cualquier evento que un socio haya creado durante una demo) y recrea los 5 placeholders originales. Tickets, mesas y reservas asociadas se borran en cascada. La cuenta y el club no se tocan.",
      promptLabel: "Escribí RESETEAR para confirmar",
      confirmLabel: "Resetear",
      tone: "danger",
    });
    if (typeof confirmText === "string" && confirmText.trim().toUpperCase() === "RESETEAR") {
      resetMutation.mutate();
    } else if (typeof confirmText === "string") {
      setActionError("La confirmación no coincide. No se hizo ningún cambio.");
    }
  };

  return (
    <div className="flex flex-col gap-4 animate-fade-in max-w-4xl">
      <header>
        <p className="nx-eyebrow mb-1">Cuenta Demo</p>
        <h1 className="nx-h1 flex items-center gap-2">
          <Sparkles size={22} className="text-accent" />
          Cuenta showroom Mixcore
        </h1>
        <p className="text-sm text-fg-muted mt-1">
          Cuenta sintética única que socios comerciales usan para mostrar la plataforma a prospectos.
          Su contenido es ficticio y queda excluido de las superficies públicas de{" "}
          <span className="font-mono">mixcore.club</span>.
        </p>
      </header>

      {actionError && (
        <div className="nx-card p-4 border-danger/30 bg-danger/5 text-danger text-sm">
          {actionError}
        </div>
      )}

      <section className="nx-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="nx-h2">Estado actual</h2>
            <p className="text-xs text-fg-muted mt-0.5">
              Snapshot del user demo y su club. El email del user es{" "}
              <span className="font-mono">demo@mixcore.demo</span>.
            </p>
          </div>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              exists ? "bg-success/10 text-success" : "bg-warning/10 text-warning"
            }`}
          >
            {exists ? "Activa" : "No existe"}
          </span>
        </div>

        {!exists ? (
          <div className="text-sm text-fg-muted">
            La cuenta demo no fue creada todavía. Configurá{" "}
            <span className="font-mono">MIXCORE_DEMO_USER_PASSWORD</span> en Secret Manager y
            tocá <b>Crear cuenta demo</b>.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="nx-eyebrow">User ID</p>
              <p className="text-sm font-medium text-fg">{status.userId}</p>
            </div>
            <div>
              <p className="nx-eyebrow">Club ID</p>
              <p className="text-sm font-medium text-fg">{status.clubId ?? "—"}</p>
            </div>
            <div>
              <p className="nx-eyebrow">Eventos</p>
              <p className="text-sm font-medium text-fg">{status.eventCount}</p>
            </div>
            <div>
              <p className="nx-eyebrow">Último reset</p>
              <p className="text-sm font-medium text-fg">{formatDate(status.lastResetAt)}</p>
            </div>
          </div>
        )}
      </section>

      {exists && canRevealPassword && (
        <section className="nx-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="nx-h2 flex items-center gap-2">
                <KeyRound size={18} className="text-accent" />
                Credenciales para socios
              </h2>
              <p className="text-xs text-fg-muted mt-0.5">
                Copiá estos valores y compartilos con quien va a hacer la demo. El password no se
                muestra en pantalla — se copia directo al portapapeles. Cada copia queda en audit.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="flex items-center justify-between p-3 rounded-md border border-border bg-bg-subtle">
              <div className="min-w-0">
                <p className="nx-eyebrow mb-1">Email</p>
                <p className="text-sm font-mono text-fg truncate">demo@mixcore.demo</p>
              </div>
              <button
                onClick={() => copyCredential("email")}
                className="nx-btn-secondary text-xs flex items-center gap-1 shrink-0 ml-3"
                aria-label="Copiar email"
              >
                {copiedField === "email" ? (
                  <>
                    <Check size={12} /> Copiado
                  </>
                ) : (
                  <>
                    <Copy size={12} /> Copiar
                  </>
                )}
              </button>
            </div>
            <div className="flex items-center justify-between p-3 rounded-md border border-border bg-bg-subtle">
              <div className="min-w-0">
                <p className="nx-eyebrow mb-1">Password</p>
                <p className="text-sm font-mono text-fg-muted">••••••••••••</p>
              </div>
              <button
                onClick={() => copyCredential("password")}
                className="nx-btn-primary text-xs flex items-center gap-1 shrink-0 ml-3"
                aria-label="Copiar password"
              >
                {copiedField === "password" ? (
                  <>
                    <Check size={12} /> Copiado
                  </>
                ) : (
                  <>
                    <Copy size={12} /> Copiar
                  </>
                )}
              </button>
            </div>
          </div>
          <p className="text-[11px] text-fg-subtle mt-3">
            Para rotar el password, actualizar el secreto{" "}
            <span className="font-mono">MIXCORE_DEMO_USER_PASSWORD</span> via Terraform o{" "}
            <span className="font-mono">gcloud secrets versions add</span>, redesplegar{" "}
            <span className="font-mono">mixcore-nexus</span> y actualizar también la fila del user
            con el nuevo hash bcrypt.
          </p>
        </section>
      )}

      <section className="nx-card p-5">
        <h2 className="nx-h2 mb-3">Acciones</h2>
        <div className="flex flex-col gap-3">
          {!exists && canSeed && (
            <div className="flex items-start justify-between gap-4 p-3 rounded-md border border-border bg-bg-subtle">
              <div className="flex-1">
                <p className="text-sm font-medium text-fg flex items-center gap-2">
                  <PlusCircle size={16} /> Crear cuenta demo
                </p>
                <p className="text-xs text-fg-muted mt-1">
                  One-shot. Crea el user, club y eventos placeholder iniciales. Requiere que{" "}
                  <span className="font-mono">MIXCORE_DEMO_USER_PASSWORD</span> esté seteado.
                </p>
              </div>
              <button
                className="nx-btn-primary text-sm"
                onClick={handleSeed}
                disabled={seedMutation.isPending}
              >
                {seedMutation.isPending ? "Creando…" : "Crear"}
              </button>
            </div>
          )}

          {exists && canReset && (
            <div className="flex items-start justify-between gap-4 p-3 rounded-md border border-border bg-bg-subtle">
              <div className="flex-1">
                <p className="text-sm font-medium text-fg flex items-center gap-2">
                  <RotateCcw size={16} /> Resetear contenido
                </p>
                <p className="text-xs text-fg-muted mt-1">
                  Borra todos los eventos actuales del club demo y recrea los 5 placeholders. Pensado
                  para correr cuando un socio dejó eventos de prueba que ensucian el demo. Confirmación
                  con typing.
                </p>
              </div>
              <button
                className="nx-btn-danger text-sm"
                onClick={handleReset}
                disabled={resetMutation.isPending}
              >
                {resetMutation.isPending ? "Reseteando…" : "Resetear"}
              </button>
            </div>
          )}

          {exists && status.userId && (
            <div className="flex items-start justify-between gap-4 p-3 rounded-md border border-border">
              <div className="flex-1">
                <p className="text-sm font-medium text-fg flex items-center gap-2">
                  <ExternalLink size={16} /> Vista pública
                </p>
                <p className="text-xs text-fg-muted mt-1">
                  Antes de mostrarle el demo a un prospecto, verificá que el filtro defensivo está
                  funcionando: ese link debe dar 404 (el organizador no debe ser visible al público).
                </p>
              </div>
              <a
                className="nx-btn-secondary text-sm flex items-center gap-1"
                href={`${DEMO_USER_PUBLIC_URL}/organizador/${status.userId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Probar <ExternalLink size={12} />
              </a>
            </div>
          )}
        </div>
      </section>

      <section className="nx-card p-5">
        <h2 className="nx-h2 mb-2">Cómo usar la cuenta demo</h2>
        <ol className="text-sm text-fg-muted space-y-2 list-decimal list-inside">
          <li>
            {canRevealPassword ? (
              <>
                Copiá las credenciales desde la card <b>Credenciales para socios</b> de arriba y
                pasalas al socio comercial.
              </>
            ) : (
              <>
                Pedile al Owner las credenciales (email + password) desde el panel{" "}
                <b>Cuenta Demo</b>. Pasalas al socio comercial.
              </>
            )}
          </li>
          <li>
            Los socios loguean en{" "}
            <a href="https://hub.mixcore.club" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
              hub.mixcore.club
            </a>{" "}
            y ven el Hub con datos ficticios.
          </li>
          <li>
            Los socios NO pueden publicar eventos reales ni mutar nada (el middleware bloquea
            cualquier escritura del user demo).
          </li>
          <li>
            Si los socios necesitan crear eventos para una demo específica, lo hacen normalmente. Al
            terminar la demo, tocá <b>Resetear</b> para limpiar.
          </li>
        </ol>
      </section>
    </div>
  );
}
